import { prepareDdlEditContext } from './prepareDdlEditContext';
// commitDdlEdit — round-trip a DSE2 `DdlEdit` through Monaco.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.2 — Commit Pipeline + Buffer Sync.
//
// Pipeline:
//   1. `resolveDdlEdit` (DSE2 `applyDdlEdit` + `emitSource`)
//   2. defense-in-depth non-overlap check on `plannedByteEdits`
//   3. either:
//      - a `setPendingEdit` setter is wired → always-on preview: stage the
//        edit in the PreviewDialog (no byte threshold) and return,
//      - else convert byte edits to Monaco `IIdentifiedSingleEditOperation[]`
//        and call `editor.executeEdits('vqe-ddl-edit', ...)` so the undo stop
//        carries the `'vqe-ddl-edit'` source label, falling back to
//        `model.pushEditOperations(null, edits, () => null)` when no editor
//        reference is supplied (used in tests + Table Designer hidden buffer).
//   4. `service.onTextChange` so the next edit sees fresh structure.
//
// `commitDdlEdit` is the ONLY module that mutates a Monaco model under
// VqeDdlEdit. Edit-action modules (addColumn / dropColumn / addIndex / etc.,
// shipped in later slices) build `DdlEdit` values and delegate.
//
// Tag `'vqe-ddl-edit'` is distinct from VQE-Edit's `'vqe-edit'` so either
// feature's undo stack can be inspected independently (Codebase Pattern).

import type { SqlEngine } from '../../../sql-grammar/model';
import type {
  DdlEdit,
  DdlEditOp,
  PlannedByteEdit,
} from '../../../sql-resolver/types';
import type {
  DependencyAnnotations,
  DependencyItem,
} from '../dependencies/types';
import { dependentsForDropEdit } from '../dependents/dependentsForDropEdit';
import { dropOpSummary, isDropOp } from '../dependents/isDropOp';
import { renderDiffHtml } from './previewDdlEdit';
import {
  resolveDdlEdit,
  type ResolveDdlEditCtx,
} from './resolveDdlEdit';

export const VQE_DDL_UNDO_SOURCE = 'vqe-ddl-edit';

export type MonacoSingleEditOperation = {
  readonly range: {
    readonly startLineNumber: number;
    readonly startColumn: number;
    readonly endLineNumber: number;
    readonly endColumn: number;
  };
  readonly text: string;
};

/** Minimal Monaco `ITextModel` surface commitDdlEdit needs. */
export type MonacoTextModelLike = {
  readonly getPositionAt: (offset: number) => {
    readonly lineNumber: number;
    readonly column: number;
  };
  readonly pushEditOperations: (
    beforeCursorState: ReadonlyArray<unknown> | null,
    edits: ReadonlyArray<MonacoSingleEditOperation>,
    cursorStateComputer: () => ReadonlyArray<unknown> | null,
  ) => unknown;
};

/** Minimal Monaco `IStandaloneCodeEditor` surface commitDdlEdit needs. */
export type MonacoEditorLike = {
  readonly executeEdits: (
    source: string,
    edits: ReadonlyArray<MonacoSingleEditOperation>,
  ) => boolean;
};

/**
 * Pending-edit shape staged in `vqeDdlStore.pendingEdit` when the threshold
 * gate fires. The preview dialog reads this; `commitPending` flushes it.
 *
 * `apply` is the thunk that completes the deferred commit — it splices the
 * planned edits into Monaco and re-syncs the resolver service. The preview
 * dialog calls it after the user clicks Apply; `cancelPending` clears the
 * staged state without invoking it.
 */
export type VqeDdlPendingEdit = {
  readonly edit: DdlEdit;
  readonly documentId: string;
  readonly statementId: string;
  readonly preview: {
    readonly emittedSource: string;
    readonly plannedEdits: ReadonlyArray<PlannedByteEdit>;
    readonly diffHtml: string;
  };
  readonly apply: () => Promise<void>;
};

/**
 * Slice H3 — pre-destructive guard hook. When the edit is a drop op AND
 * `getDependents` returns at least one item, `commitDdlEdit` stages a
 * `VqeDdlPendingBlastRadius` via `setPendingBlastRadius` and waits for
 * the user to confirm. The staged record's `proceed()` thunk re-enters the
 * commit pipeline with `setPendingBlastRadius` deliberately omitted so the
 * gate doesn't re-prompt — drops then fall into the normal preview-or-direct
 * path.
 *
 * The host (TableDesigner / inline-DDL toolbar) supplies `getDependents`;
 * commitDdlEdit calls it synchronously with the current `DependencyAnnotations`
 * snapshot the host has cached. Skipping when omitted keeps the gate opt-in
 * and keeps existing unit tests passing without per-test stubbing.
 */
export type BlastRadiusGate = {
  readonly setPendingBlastRadius: (
    pending: import('../store/vqeDdlStore').VqeDdlPendingBlastRadius,
  ) => void;
  /** Latest `DependencyAnnotations` for the object the edit targets — the
   *  same payload the sidebar pip already showed the user. */
  readonly annotations: DependencyAnnotations;
  /** VDE-9 fail-safe gate. True only when `annotations` is a CONFIRMED query
   *  success for the current target. When false the annotations are an empty
   *  placeholder (loading / fetching / disabled / errored) — dependents are
   *  therefore UNKNOWN, so a destructive drop must FORCE the blast-radius
   *  confirmation rather than commit silently. Never derive "no dependents"
   *  from `annotations` alone. */
  readonly annotationsReady: boolean;
  /** Identifies which surface staged the gate ("inline" | "designer"). */
  readonly source: 'inline' | 'designer';
  /** Object the panel header references. May be null pre-parse. */
  readonly target: {
    readonly schema: string | null;
    readonly objectName: string | null;
  };
  /** Optional per-edit override: a host that knows better which dependents
   *  apply to a given edit (e.g. drop-table on a table whose annotations were
   *  scoped to a different name) supplies its own filter. Defaults to
   *  `dependentsForDropEdit`. */
  readonly resolveDependents?: (
    edit: DdlEdit,
    annotations: DependencyAnnotations,
  ) => readonly DependencyItem[];
};

export type CommitDdlEditCtx = ResolveDdlEditCtx & {
  readonly model: MonacoTextModelLike;
  readonly editor?: MonacoEditorLike;
  /**
   * Optional pending-edit setter. Always-on preview: when provided,
   * `commitDdlEdit` ALWAYS stages the edit for preview (the diff dialog)
   * instead of splicing the buffer directly — there is no byte threshold.
   * Tests pass a stub; production wires this to
   * `useVqeDdlStore.getState().setPendingEdit`. Omit it (unit tests, recovery
   * flows) to apply the edit straight to the buffer.
   */
  readonly setPendingEdit?: (pending: VqeDdlPendingEdit) => void;
  /**
   * Optional pre-destructive guard wiring. When supplied, drop ops with
   * dependents stage a `VqeDdlPendingBlastRadius` instead of applying. Omit
   * to bypass the guard (e.g. internal tests, or one-shot recovery flows).
   */
  readonly blastRadius?: BlastRadiusGate;
};

export async function commitDdlEdit(
  edit: DdlEdit,
  ctx: CommitDdlEditCtx,
): Promise<void> {
  // Slice H3 pre-destructive guard. Fires before resolve so a drop with
  // dependents never has its planned edits computed twice. The gate stages
  // a `VqeDdlPendingBlastRadius` whose `proceed` thunk re-enters
  // `commitDdlEdit` with the gate stripped — falling into preview-or-direct.
  if (ctx.blastRadius && isDropOp(edit)) {
    const ready = ctx.blastRadius.annotationsReady;
    const resolveDependents =
      ctx.blastRadius.resolveDependents ?? dependentsForDropEdit;
    // VDE-9 fail-safe: only trust `annotations` when the query confirmed a
    // success for this target. When not ready, dependents are UNKNOWN — keep
    // the list empty but still force the confirmation below.
    const dependents = ready
      ? resolveDependents(edit, ctx.blastRadius.annotations)
      : [];
    if (!ready || dependents.length > 0) {
      const summary = dropOpSummary(edit);
      const proceed = async (): Promise<void> => {
        const { blastRadius: _drop, ...rest } = ctx;
        void _drop;
        await commitDdlEdit(edit, rest);
      };
      ctx.blastRadius.setPendingBlastRadius({
        edit,
        documentId: ctx.documentId,
        statementId: ctx.statementId,
        source: ctx.blastRadius.source,
        target: {
          schema: ctx.blastRadius.target.schema,
          objectName: ctx.blastRadius.target.objectName,
          actionLabel: summary.action,
          targetLabel: summary.target,
        },
        dependents,
        // Flag the unknown case so the panel says dependents could NOT be
        // verified instead of falsely claiming "No dependents recorded.".
        ...(ready ? {} : { dependentsUnverified: true }),
        proceed,
      });
      return;
    }
  }

  const resolved = resolveDdlEdit(edit, await prepareDdlEditContext(edit, ctx));
  const { modified, emittedSource, originalSource } = resolved;

  if (modified.plannedEdits.length === 0) return;

  // Coalesce a delete + zero-width insert at the same start offset into a
  // single replacement before sending edits to Monaco. DSE2's planners emit
  // this shape for ops that delete a span and re-insert at the same anchor;
  // semantically it is one replacement. Coalescing also keeps
  // `assertNonOverlappingByteEdits` honest — the pair counts as a true overlap
  // in the naive byte-range sense.
  const plannedEdits = coalesceCoincidentEdits(modified.plannedEdits);

  // Defense in depth — `emitSource` already validates non-overlap before
  // splicing, but we re-check here so an invariant-violating edit can't reach
  // the Monaco buffer even if SR-Edit / DSE2 ever regress.
  assertNonOverlappingByteEdits(plannedEdits);

  // Always-on preview (no byte threshold). Every Designer / inline-DDL edit
  // stages a diff in the PreviewDialog before touching the buffer, so the user
  // confirms the exact SQL VqeDdlEdit is about to splice. Surfaces that wire a
  // `setPendingEdit` setter (Table Designer, inline DDL toolbar) funnel through
  // the dialog; surfaces without one (unit tests, recovery flows) apply
  // directly. The always-on pre-destructive blast-radius guard is handled
  // above and is independent of this preview.
  if (typeof ctx.setPendingEdit === 'function') {
    const apply = async (): Promise<void> => {
      applyResolvedEditToMonaco(plannedEdits, ctx);
      await ctx.service.onTextChange(ctx.documentId, emittedSource);
    };
    ctx.setPendingEdit({
      edit,
      documentId: ctx.documentId,
      statementId: ctx.statementId,
      preview: {
        emittedSource,
        plannedEdits,
        diffHtml: renderDiffHtml(originalSource, plannedEdits),
      },
      apply,
    });
    return;
  }

  applyResolvedEditToMonaco(plannedEdits, ctx);
  await ctx.service.onTextChange(ctx.documentId, emittedSource);
  // VDE-3 — the edit truly reached the buffer (this is the single
  // chokepoint: the preview/staging path returns earlier via
  // `setPendingEdit`, and the blast-radius gate stalls via
  // `setPendingBlastRadius`, so neither is ever counted here; only the
  // `proceed()`/`apply()` re-entry that flows through this line fires).
  // `edit.op` is the low-cardinality `DdlEdit` discriminant — no PII.
  // NOTE: the materialized-view *refresh* action (op
  // `refresh-materialized-view`) does NOT flow through `commitDdlEdit` — it
  // executes directly against the connection (`executeRefreshMaterializedView`)
  // and is tracked separately at its own success point in
  // `render/common/MatViewRefreshFormBody.tsx`, so this is not a gap.
  trackDdlCommit(edit.op, ctx.service.engineId, ctx.blastRadius?.source);
}

/** Fire-and-forget commit analytics (non-React context). */
function trackDdlCommit(
  op: DdlEditOp,
  engine: SqlEngine,
  source?: 'inline' | 'designer',
): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;
  window.electronAPI.analytics
    .track('feature:vqe_ddl_visual_edit', {
      op,
      engine,
      ...(source ? { source } : {}),
    })
    .catch(() => {
      // Silently ignore analytics errors — they must not affect editing.
    });
}

function coalesceCoincidentEdits(
  edits: ReadonlyArray<PlannedByteEdit>,
): ReadonlyArray<PlannedByteEdit> {
  if (edits.length < 2) return edits;
  const sorted = [...edits].sort((a, b) => {
    if (a.range.startOffset !== b.range.startOffset) {
      return a.range.startOffset - b.range.startOffset;
    }
    // Wider range first, so a delete sorts before a zero-width insert at
    // the same start offset (lets the merged result use the delete's range).
    return b.range.endOffset - a.range.endOffset;
  });
  const out: PlannedByteEdit[] = [];
  for (const edit of sorted) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.range.startOffset === edit.range.startOffset &&
      edit.range.startOffset === edit.range.endOffset
    ) {
      out[out.length - 1] = {
        range: prev.range,
        replacement: prev.replacement + edit.replacement,
        rationale: `${prev.rationale}+${edit.rationale}`,
      };
      continue;
    }
    out.push(edit);
  }
  return out;
}

function applyResolvedEditToMonaco(
  plannedEdits: ReadonlyArray<PlannedByteEdit>,
  ctx: CommitDdlEditCtx,
): void {
  const monacoEdits = plannedEdits.map((edit) =>
    byteEditToMonacoEdit(edit, ctx.model),
  );
  if (ctx.editor) {
    ctx.editor.executeEdits(VQE_DDL_UNDO_SOURCE, monacoEdits);
    return;
  }
  ctx.model.pushEditOperations(null, monacoEdits, () => null);
}

function byteEditToMonacoEdit(
  edit: PlannedByteEdit,
  model: MonacoTextModelLike,
): MonacoSingleEditOperation {
  const start = model.getPositionAt(edit.range.startOffset);
  const end = model.getPositionAt(edit.range.endOffset);
  return {
    range: {
      startLineNumber: start.lineNumber,
      startColumn: start.column,
      endLineNumber: end.lineNumber,
      endColumn: end.column,
    },
    text: edit.replacement,
  };
}

function assertNonOverlappingByteEdits(
  edits: ReadonlyArray<PlannedByteEdit>,
): void {
  const sorted = [...edits].sort(
    (a, b) => a.range.startOffset - b.range.startOffset,
  );
  for (let i = 0; i + 1 < sorted.length; i++) {
    const cur = sorted[i]!;
    const nxt = sorted[i + 1]!;
    if (nxt.range.startOffset < cur.range.endOffset) {
      throw new Error(
        `commitDdlEdit: overlapping plannedByteEdits — [${cur.range.startOffset}, ${cur.range.endOffset}) and [${nxt.range.startOffset}, ${nxt.range.endOffset})`,
      );
    }
  }
}
